import React, { PureComponent } from 'react';
import { array, bool, number, object, objectOf, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Utils from 'yoda-core-components/lib/helpers/Utils/Utils';
import withReducerSaga from 'yoda-site-components/lib/hoc/ReducerRegistry/withReducerSaga';
import { hasBreadCrumbLinks } from '../../helpers/GalleryHelper';

import { getAutoCorrectedMessage, getH1Tag } from '../../selectors/SeoTags';
import { getInteractiveVizNavFromIrisDataState } from '../../selectors/InteractiveVizNav';
import {
    selectEnableTextTransform,
    selectStatusCheck,
    selectHideBreadCrumbsWhenNoNavigation,
} from '../../selectors/fromState';
import getBreadCrumbInfo from '../../selectors/BreadCrumbs';
import {
    isBrandPage,
    getLastFilter,
    getPageURLFromAPIURL,
    getSearchTermFromUrl,
    updateQueryStringParameter,
} from '../../utils';

import rerenderReducer from '../../reducers/rerenderReducer';
import * as styles from './GalleryTitle.css';

const cx = classNames.bind(styles);

/* istanbul ignore next */
export class GalleryTitle extends PureComponent {
    static defaultProps = {
        autoCorrectedMessage: '',
        countOfSearchResults: 0,
        deviceType: { isDesktop: true },
        enableKeywordRedirectMessage: false,
        enableTextTransform: false,
        h1Tag: '',
        interactiveVizNav: {},
        keywordRedirectMessage: '',
        pageType: '',
        partialMatchInfo: {},
        rerender: false,
        searchStatus: null,
        seoTags: {},
        showSearchResultsCountSubtitle: false,
        breadCrumbInfo: [],
        hideBreadCrumbsWhenNoNavigation: false,
        isNative: false,
    };

    static propTypes = {
        breadCrumbInfo: arrayOf(object),
        APIResponse: bool.isRequired,
        autoCorrectedMessage: string,
        countOfSearchResults: number,
        deviceType: objectOf(bool),
        enableTextTransform: bool,
        enableKeywordRedirectMessage: bool,
        h1Tag: string,
        interactiveVizNav: object,
        keywordRedirectMessage: string,
        pageType: string,
        partialMatchInfo: shape({
            fullMatchTerms: array,
            originalSearchTerm: string,
        }),
        rerender: bool,
        searchStatus: number,
        seoTags: object,
        showSearchResultsCountSubtitle: bool,
        hideBreadCrumbsWhenNoNavigation: bool,
        isNative: bool,
    };

    constructor() {
        super();

        this.getPageTitle = this.getPageTitle.bind(this);
        this.scrollTitleIntoView = this.scrollTitleIntoView.bind(this);
    }

    componentDidUpdate() {
        const {
            breadCrumbInfo,
            deviceType,
            interactiveVizNav,
            rerender,
            hideBreadCrumbsWhenNoNavigation,
            isNative,
        } = this.props;
        try {
            if (rerender && this.titleNode) {
                const { name = '' } = getLastFilter() || {};
                // MNPDPYODA-12261 do not scroll to gallery Title when same day pickup filter got selected
                if (name === 'same day pickup' || !isEmpty(interactiveVizNav)) {
                    this.scrollTitleIntoView('#gallery-product-list');
                } else if (document.getElementById('inspirational-block')) {
                    this.scrollTitleIntoView('#inspirational-block');
                } else if (
                    !isNative &&
                    hasBreadCrumbLinks(breadCrumbInfo, deviceType, hideBreadCrumbsWhenNoNavigation)
                ) {
                    this.scrollTitleIntoView('[data-automation-id="product-breadcrumbs"]');
                } else {
                    this.scrollTitleIntoView('#gallery-title');
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }

    getPageTitle = () => {
        const {
            enableKeywordRedirectMessage,
            enableTextTransform,
            h1Tag,
            keywordRedirectMessage,
            pageType,
            seoTags: { h1Data = '' },
        } = this.props;

        let headerText = h1Tag || h1Data;
        headerText = enableTextTransform ? headerText.toLowerCase() : headerText;

        const isSearchPage = pageType === 'Search';
        const pageTitle = isSearchPage ? getSearchTermFromUrl().toLowerCase() : headerText;
        const textTransform =
            isSearchPage || (!isSearchPage && enableTextTransform) ? 'capitalize' : 'normal-case';
        const titleClasses = [
            'font-open-sans-bold',
            'text-center',
            'text-gray-600',
            'text-medium',
            'w-full',
            textTransform,
            cx('gallery-title'),
        ].join(' ');

        if (!pageTitle) {
            return null;
        }
        return (
            <div className={cx('title-container')}>
                {enableKeywordRedirectMessage && keywordRedirectMessage.length > 0
                    ? this.renderKeywordRedirectMessage(keywordRedirectMessage)
                    : null}
                <div
                    className="flex flex-row flex-wrap"
                    id="gallery-title"
                    ref={(titleNode) => {
                        this.titleNode = titleNode;
                    }}
                >
                    {!keywordRedirectMessage || !enableKeywordRedirectMessage
                        ? (isSearchPage && this.renderSearchPageTitle(pageTitle, titleClasses)) ||
                          this.renderGalleryPageTitle(pageTitle, titleClasses)
                        : null}
                </div>
            </div>
        );
    };

    scrollTitleIntoView = (scrollElement) => {
        const {
            deviceType: { isDesktop },
            isNative,
        } = this.props;
        const BUFFER = 10; /* buffer height between the title and header */
        const scrollable = document.querySelector(scrollElement);
        const STICKY_HEADER_HEIGHT =
            !isNative && document.getElementById('header').getBoundingClientRect().height;
        let topOfElement = window.pageYOffset + (scrollable.getBoundingClientRect().top - BUFFER);

        if (isNative) {
            topOfElement = window.pageYOffset + (scrollable.getBoundingClientRect().top - BUFFER);
        } else if (!isDesktop) {
            topOfElement =
                window.pageYOffset +
                (scrollable.getBoundingClientRect().top - STICKY_HEADER_HEIGHT - BUFFER);
        }

        const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

        if (supportsNativeSmoothScroll) {
            window.scroll({ top: topOfElement, behavior: 'smooth' });
            document.documentElement.scrollTop = topOfElement;
        } else {
            window.scroll({ top: topOfElement, behavior: 'smooth' });
        }

        /*
         * YODATOF-313 - window scroll does not anchor to the elements needed
         */
        if (Utils.detectisIE()) {
            document.documentElement.scrollTop = topOfElement;
        }
        if (Utils.detectisEdge()) {
            document.body.scrollTop = topOfElement;
        }
    };

    renderSearchPageTitle = (pageTitle, titleClasses) => {
        const {
            autoCorrectedMessage,
            countOfSearchResults,
            partialMatchInfo,
            showSearchResultsCountSubtitle,
        } = this.props;
        const isPartialMatch = !isEmpty(partialMatchInfo);
        const subTitleClasses = ['font-open-sans', 'text-center', 'text-small', 'w-full'].join(' ');

        return isPartialMatch
            ? this.renderPartialMatchResult(
                  countOfSearchResults,
                  pageTitle,
                  partialMatchInfo,
                  subTitleClasses,
                  titleClasses
              )
            : this.renderFullMatchResult(
                  autoCorrectedMessage,
                  countOfSearchResults,
                  pageTitle,
                  showSearchResultsCountSubtitle,
                  subTitleClasses,
                  titleClasses
              );
    };

    renderPartialMatchResult = (
        countOfSearchResults,
        pageTitle,
        partialMatchInfo,
        subTitleClasses,
        titleClasses
    ) => {
        const { fullMatchTerms } = partialMatchInfo;
        const textDecoration =
            fullMatchTerms.length === 1
                ? 'no-underline pointer-events-none'
                : 'hover:underline active:underline focused:underline underline';
        const partialMatchTermContainerClasses = [
            'capitalize',
            'mb-1 mt-1',
            'text-center',
            'w-full',
            cx('partialMatchTermContainer'),
        ].join(' ');

        return (
            <>
                <h2 className={subTitleClasses} data-automation-id="subheaderTag">
                    {`Here are ${countOfSearchResults} results for`}
                </h2>
                <div className={partialMatchTermContainerClasses}>
                    {fullMatchTerms.map((term, index) => {
                        const partialMatchUrl = getPageURLFromAPIURL(term.fullMatchUrl);
                        const newSearchUrl = updateQueryStringParameter(
                            partialMatchUrl,
                            'searchType',
                            'partialFullMatchTerm'
                        );

                        return (
                            <>
                                {fullMatchTerms.length > 1 && index > 0 && <span> , </span>}
                                <a
                                    className={`${textDecoration} ${titleClasses}`}
                                    data-automation-id="headerTag"
                                    href={newSearchUrl}
                                    key={term.fullMatchTerm}
                                >
                                    {term.fullMatchTerm}
                                </a>
                            </>
                        );
                    })}
                </div>
                <h2 className={subTitleClasses} data-automation-id="subheaderTag">
                    {`We found 0 results for ${pageTitle}`}
                </h2>
            </>
        );
    };

    renderFullMatchResult = (
        autoCorrectedMessage,
        countOfSearchResults,
        pageTitle,
        showSearchResultsCountSubtitle,
        subTitleClasses,
        titleClasses
    ) => (
        <>
            <h1 className={titleClasses} data-automation-id="headerTag">
                {`\x22${autoCorrectedMessage || pageTitle}\x22`}
            </h1>
            {autoCorrectedMessage && (
                <h2 className={subTitleClasses} data-automation-id="subheaderTag">
                    {`You searched for \x22${pageTitle}\x22`}
                </h2>
            )}
            {showSearchResultsCountSubtitle && (
                <h2 className={subTitleClasses} data-automation-id="subheaderTag">
                    {`${countOfSearchResults} Results`}
                </h2>
            )}
        </>
    );

    renderGalleryPageTitle = (pageTitle, titleClasses) => (
        <h1 className={titleClasses} data-automation-id="headerTag">
            {pageTitle}
        </h1>
    );

    renderKeywordRedirectMessage = (message) => {
        if (message.length > 0) {
            const messageWrapper = ['flex flex-row flex-wrap', cx('message-wrapper')].join(' ');
            const messageClass = ['font-open-sans', 'text-center', 'text-small', 'w-full'].join(
                ' '
            );
            const headingClass = [
                'font-open-sans-bold',
                'text-center',
                'text-medium',
                'w-full',
                cx('message-heading'),
            ].join(' ');
            const messageTop = message.split('"')[0];
            const messageSearchTerm = message.split('"')[1];
            const messageBottom = message.split('"')[2];
            return (
                <div className={messageWrapper}>
                    <p className={messageClass}>{messageTop && messageTop}</p>
                    <p className={headingClass}>{messageSearchTerm && `"${messageSearchTerm}"`}</p>
                    <p className={messageClass}>{messageBottom && messageBottom}</p>
                </div>
            );
        }
        return '';
    };

    render() {
        const { APIResponse, countOfSearchResults, searchStatus, pageType } = this.props;
        const isSearchPage = pageType === 'Search';
        const isGalleryPage = pageType === 'Gallery';
        if (isSearchPage) {
            if (APIResponse && searchStatus !== 403 && countOfSearchResults > 0) {
                return this.getPageTitle();
            }
        }
        if (isGalleryPage) {
            if (APIResponse && searchStatus !== 403) {
                if (isBrandPage()) {
                    return this.getPageTitle();
                }
                if (countOfSearchResults > 0) {
                    return this.getPageTitle();
                }
            }
        }
        return null;
    }
}

const mapStateToProps = (state) => ({
    breadCrumbInfo: getBreadCrumbInfo(state),
    APIResponse: state.commonData.gallery,
    autoCorrectedMessage: getAutoCorrectedMessage(state),
    countOfSearchResults: state.countOfSearchResults,
    deviceType: get(state, 'context.deviceType', {}),
    enableMultiSelectFilters: get(state, 'context.featureFlags.enableMultiSelectFilters', false),
    scrollToTitle: get(state, 'multiSelectFilter.scrollToTitle', true),
    enableTextTransform: selectEnableTextTransform(state),
    enableKeywordRedirectMessage: get(
        state,
        'context.featureFlags.enableKeywordRedirectMessage',
        false
    ),
    h1Tag: getH1Tag(state),
    interactiveVizNav: getInteractiveVizNavFromIrisDataState(state),
    isNative: get(state, 'context.isNative', false),
    keywordRedirectMessage: get(state, 'commonData.keywordRedirectMessage', ''),
    multiSelectedFacetsList: get(state, 'multiSelectFilter.multiSelectedFacetsList', {}),
    partialMatchInfo: state.partialMatchInfo,
    rerender: get(state, 'rerender'),
    searchStatus: selectStatusCheck(state),
    seoTags: state.seoTags,
    showSearchResultsCountSubtitle: get(
        state,
        'context.featureFlags.showSearchResultsCountSubtitle',
        false
    ),
    sortOptions: get(state, 'sortOptions.sortOptions', []),
    hideBreadCrumbsWhenNoNavigation: selectHideBreadCrumbsWhenNoNavigation(state),
});

export default compose(
    withReducerSaga([rerenderReducer], []),
    connect(mapStateToProps)
)(GalleryTitle);
